import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, Input, InputNumber, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import { hrApi, useHr } from './hrStore';
import {
  EDUCATION, OUTCOME, REJECT_REASONS, SOURCE, STAGE, STAGES, WITHDRAW_REASONS, branchById, candById, candidateDupCheck, fmtD, fmtMoney,
  offerNeedsApproval, positionById, reqById, reqHired, toDay, type Stage,
  empById, licenseBlocker, noShowCheck, pendingInterview, poolSuggestion,
} from './hrLogic';
import { ConsentLine, InterviewPanel, LicensePanel, NoShowModal, eraseCandidate } from './RecruitPanels';
import { Avatar, Field, Notes, StageTag, empUrl, fmtDateTime, reqUrl, useHrPerms } from './hrUi';
import { DateInput, MoneyInput } from './hrModals';

/** Hồ sơ ứng viên: tiến trình, đánh giá từng vòng và các thao tác chuyển bước / offer / nhận việc */
export default function CandidateDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const s = useHr();
  const navigate = useNavigate();
  const { me, canRecruit, canApprove } = useHrPerms();
  const c = candById(s, id || undefined);
  const r = reqById(s, c?.requisitionId);
  const [score, setScore] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [offer, setOffer] = useState<{ salary: number; startDate: string } | null>(null);
  const [closing, setClosing] = useState<'rejected' | 'withdrawn' | 'declined' | null>(null);
  const [reason, setReason] = useState<string>('');
  const [noShow, setNoShow] = useState<string | null>(null);
  const today = toDay();
  if (!c || !r) return <Drawer open={!!id} onClose={onClose} title="Ứng viên" width={560}><div className="text-gray-400">Không tìm thấy ứng viên</div></Drawer>;

  const pos = positionById(s, r.positionId);
  const idx = STAGES.indexOf(c.stage);
  const next = STAGES[idx + 1] as Stage | undefined;
  const dup = candidateDupCheck(s, { phone: c.phone, cccd: c.cccd, id: c.id }, today);
  const open = c.outcome === 'open';
  const reqOpen = r.status === 'approved';
  const full = reqHired(s, r) >= r.qty;
  const needScore = c.stage === 'interview' || c.stage === 'skill';
  const pending = pendingInterview(c);
  const waitInterview = !!pending && pending.at > Date.now();
  const licBlock = licenseBlocker(s, c, today);
  const emp = c.outcome === 'hired' ? empById(s, c.empId) : undefined;
  const canNoShow = !!emp && noShowCheck(s, emp, today).ok;
  const reset = () => { setScore(null); setNote(''); setOffer(null); setClosing(null); setReason(''); };
  const run = (res: { ok: boolean; errors?: string[]; warnings?: string[] }, ok: string) => {
    if (!res.ok) { Modal.error({ title: 'Chưa thực hiện được', content: res.errors?.join('\n') }); return false; }
    message.success(ok);
    res.warnings?.forEach(w => message.warning(w));
    reset();
    return true;
  };

  const advance = () => run(hrApi.advance(c.id, me, { score: score || undefined, note: note || undefined }), `Đã chuyển sang ${STAGE[next!].label}`);
  const sendOffer = () => offer && run(hrApi.makeOffer(c.id, offer.salary, offer.startDate, me, { note: note || undefined, score: score || undefined }), 'Đã lập offer');
  const close = () => {
    if (closing === 'declined') run(hrApi.offerResult(c.id, false, me, reason), 'Đã ghi nhận ứng viên từ chối offer');
    else if (closing) run(hrApi.close(c.id, closing, reason, me), closing === 'rejected' ? 'Đã loại ứng viên' : 'Đã ghi nhận ứng viên rút');
  };
  const hire = () => {
    const res = hrApi.hire(c.id, me);
    if (run(res, 'Đã tạo hồ sơ nhân sự và thỏa thuận thử việc (chưa ký)')) navigate(empUrl(res.id!, 'contracts'));
  };

  const current = c.outcome === 'hired' ? STAGES.length - 1 : idx;
  return (
    <Drawer open={!!id} onClose={() => { reset(); onClose(); }} width={600} title={
      <div className="flex items-center gap-3">
        <Avatar name={c.name} size={10} />
        <div className="min-w-0">
          <div className="font-semibold">{c.name}</div>
          <div className="text-xs text-gray-500 font-normal">
            <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => navigate(reqUrl(r.id))}>{r.code}</button> · {pos?.name} · {branchById(s, r.branchId)?.short}
          </div>
        </div>
        <div className="ml-auto"><StageTag c={c} /></div>
      </div>
    }>
      <div className="space-y-5">
        {/* Tiến trình gọn cho drawer hẹp: 6 bước chia đều, nhãn dưới chấm */}
        <ol className="grid grid-cols-6" aria-label="Tiến trình tuyển dụng">
          {STAGES.map((st, i) => {
            const failed = (c.outcome === 'rejected' || c.outcome === 'withdrawn') && i === current;
            const passed = i < current || c.outcome === 'hired';
            const now = i === current && c.outcome === 'open';
            return (
              <li key={st} className="relative flex flex-col items-center gap-1 text-center" aria-current={now ? 'step' : undefined}>
                {i > 0 && <span className={`absolute top-[11px] right-1/2 w-full h-px ${i <= current ? 'bg-[#3e91ab]' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                <span className={`relative z-[1] w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  failed ? 'bg-rose-500 text-white' : passed ? 'bg-[#e3f0f4] text-[#2c6b7f]' : now ? 'bg-[#3e91ab] text-white' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'}`}>
                  {failed ? '✕' : passed ? '✓' : i + 1}
                </span>
                <span className={`text-[11px] leading-tight ${now || failed ? 'font-semibold text-gray-800 dark:text-gray-100' : 'text-gray-500'}`}>{STAGE[st].short}</span>
              </li>
            );
          })}
        </ol>

        <Notes errors={c.outcome === 'open' ? dup.errors : []} warnings={dup.warnings} />

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {[
            ['Điện thoại', c.phone], ['Email', c.email || '—'], ['CCCD', c.cccd || '—'], ['Giới tính', c.gender],
            ['Trình độ', EDUCATION[c.education]], ['Kinh nghiệm', c.experienceYears !== undefined ? `${c.experienceYears} năm` : '—'],
            ['Lương mong muốn', c.expectedSalary ? fmtMoney(c.expectedSalary) : '—'], ['Nguồn', SOURCE[c.source]],
            ['Ngày ứng tuyển', fmtDateTime(c.appliedAt)], ['Khung lương vị trí', `${fmtMoney(r.salaryMin)} – ${fmtMoney(r.salaryMax)}`],
          ].map(([l, v]) => <div key={l}><div className="text-[11px] text-gray-400">{l}</div><div className="text-gray-800 dark:text-gray-200">{v}</div></div>)}
        </div>
        {c.note && <div className="text-sm text-gray-600 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">{c.note}</div>}
        <ConsentLine c={c} canEdit={canRecruit} />
        {c.noShow && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">Không đến nhận việc ({c.noShow.reason}) — đã hủy hồ sơ nhân sự {c.noShow.empCode} · {fmtDateTime(c.noShow.at)} · {c.noShow.by}</div>}

        {(needScore || (c.interviews || []).length > 0) && <InterviewPanel c={c} canEdit={canRecruit && open} />}
        {pos?.license && (open || c.license) && <LicensePanel c={c} need={pos.license} canEdit={canRecruit} />}

        {c.offer && (
          <div className={`rounded-lg border px-3 py-2 text-sm ${c.offer.status === 'need_approval' ? 'border-amber-200 bg-amber-50' : c.offer.status === 'declined' ? 'border-gray-200 bg-gray-50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className="font-medium">Offer {fmtMoney(c.offer.salary)} · nhận việc {fmtD(c.offer.startDate)}</div>
            <div className="text-xs text-gray-600">
              {{ need_approval: 'Ngoài khung lương — chờ người có quyền duyệt', sent: 'Đã gửi, chờ ứng viên trả lời', accepted: 'Ứng viên đã nhận offer', declined: `Ứng viên từ chối: ${c.offer.declineReason || c.closeReason || ''}` }[c.offer.status]}
              {c.offer.approvedBy ? ` · duyệt bởi ${c.offer.approvedBy}` : ''}
            </div>
          </div>
        )}
        {c.outcome !== 'open' && c.outcome !== 'hired' && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800/40 dark:border-gray-700 px-3 py-2 text-sm space-y-1.5">
            <div><b>{OUTCOME[c.outcome].label}</b> ở bước {STAGE[c.stage].label}{c.closeReason ? ` — ${c.closeReason}` : ''}{c.closedAt ? ` · ${fmtDateTime(c.closedAt)}` : ''}</div>
            {canRecruit && !c.anonymizedAt && !c.noShow && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {c.pool ? <span className="text-emerald-700">Đã lưu vào kho ứng viên · {c.pool.by}</span> : poolSuggestion(c) && !c.poolHidden ? <span className="text-gray-500">Gợi ý giữ lại: {poolSuggestion(c)}</span> : null}
                <Button size="small" onClick={() => run(hrApi.setPool(c.id, !c.pool, me), c.pool ? 'Đã bỏ khỏi kho ứng viên' : 'Đã lưu vào kho ứng viên')}>{c.pool ? 'Bỏ khỏi kho' : 'Lưu vào kho ứng viên'}</Button>
              </div>
            )}
          </div>
        )}
        {c.fromCandidateId && <div className="text-xs text-gray-500">Mời lại từ kho ứng viên — <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => navigate(`/candidates-view?id=${c.fromCandidateId}&stage=all`)}>xem hồ sơ cũ</button></div>}
        {c.outcome === 'hired' && c.empId && (
          <div className="flex gap-2">
            <Button block onClick={() => navigate(empUrl(c.empId!))}>Mở hồ sơ nhân sự</Button>
            {canNoShow && canRecruit && <Button danger onClick={() => setNoShow(c.empId!)}>Không đến nhận việc</Button>}
          </div>
        )}

        {/* ===== Thao tác ===== */}
        {open && canRecruit && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-3">
            {!reqOpen && <Notes warnings={['Yêu cầu tuyển dụng đã đóng — không xử lý tiếp được']} />}
            {reqOpen && next && next !== 'offer' && next !== 'hired' && !closing && (
              <>
                <div className="text-sm font-medium">Kết quả {STAGE[c.stage].label.toLowerCase()} → chuyển sang <b>{STAGE[next].label}</b></div>
                <div className="flex gap-2">
                  {needScore && <InputNumber min={1} max={10} placeholder="Điểm 1–10" value={score} onChange={setScore} className="!w-[120px]" disabled={waitInterview} />}
                  <Input placeholder="Nhận xét" value={note} onChange={e => setNote(e.target.value)} disabled={waitInterview} />
                </div>
                {waitInterview && <div className="text-xs text-gray-500">Ghi kết quả sau giờ phỏng vấn ({dayjs(pending!.at).format('HH:mm DD/MM')})</div>}
                <Button type="primary" onClick={advance} disabled={(needScore && !score) || waitInterview}>Đạt, chuyển bước</Button>
              </>
            )}
            {reqOpen && (c.stage === 'skill' || c.offer?.status === 'need_approval') && !closing && (
              offer ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Lương chính thức offer"><MoneyInput value={offer.salary} onChange={salary => setOffer({ ...offer, salary })} /></Field>
                    <Field label="Ngày nhận việc"><DateInput value={offer.startDate} onChange={startDate => setOffer({ ...offer, startDate })} min={today} /></Field>
                  </div>
                  {c.stage === 'skill' && (
                    <div className="flex gap-2">
                      <InputNumber min={1} max={10} placeholder="Điểm chuyên môn" value={score} onChange={setScore} className="!w-[150px]" />
                      <Input placeholder="Nhận xét vòng chuyên môn" value={note} onChange={e => setNote(e.target.value)} />
                    </div>
                  )}
                  <Notes errors={licBlock ? [licBlock] : []} warnings={offerNeedsApproval(r, offer.salary) ? ['Ngoài khung lương của yêu cầu — offer cần được duyệt trước khi gửi'] : full ? ['Yêu cầu đã đủ chỉ tiêu'] : []} />
                  <div className="flex gap-2"><Button type="primary" onClick={sendOffer} disabled={!!licBlock || (c.stage === 'skill' && waitInterview)}>Lập offer</Button><Button onClick={() => setOffer(null)}>Thôi</Button></div>
                </div>
              ) : (
                <Button onClick={() => setOffer({ salary: c.offer?.salary || Math.min(r.salaryMax, Math.max(r.salaryMin, c.expectedSalary || r.salaryMin)), startDate: c.offer?.startDate || dayjs().add(7, 'day').format('YYYY-MM-DD') })}>
                  {c.offer ? 'Sửa offer' : 'Đạt chuyên môn — lập offer'}
                </Button>
              )
            )}
            {c.offer?.status === 'need_approval' && canApprove && !closing && (
              <Button type="primary" onClick={() => run(hrApi.approveOffer(c.id, me), 'Đã duyệt offer')}>Duyệt offer ngoài khung</Button>
            )}
            {c.offer?.status === 'sent' && !closing && (
              <div className="flex gap-2">
                <Button type="primary" onClick={() => run(hrApi.offerResult(c.id, true, me), 'Ứng viên đã nhận offer')}>Ứng viên nhận offer</Button>
                <Button onClick={() => { setClosing('declined'); setReason(WITHDRAW_REASONS[0]); }}>Ứng viên từ chối</Button>
              </div>
            )}
            {c.offer?.status === 'accepted' && reqOpen && (
              <div className="space-y-2">
                <div className="text-xs text-gray-500">Nhận việc tạo hồ sơ nhân sự, thỏa thuận thử việc (lương 85% lương offer, chưa ký) và tự đóng yêu cầu khi đủ chỉ tiêu.</div>
                <Button type="primary" onClick={hire} disabled={full}>Nhận việc ngày {fmtD(c.offer.startDate)}</Button>
              </div>
            )}
            {closing ? (
              <div className="space-y-2">
                <Field label={closing === 'rejected' ? 'Lý do loại' : 'Lý do ứng viên từ chối / rút'}>
                  <Select className="w-full" value={reason || undefined} onChange={setReason} options={(closing === 'rejected' ? REJECT_REASONS : WITHDRAW_REASONS).map(x => ({ value: x, label: x }))} />
                </Field>
                <div className="flex gap-2"><Button danger type="primary" onClick={close} disabled={!reason}>Xác nhận</Button><Button onClick={() => setClosing(null)}>Thôi</Button></div>
              </div>
            ) : c.offer?.status !== 'accepted' && (
              <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <Button size="small" danger onClick={() => { setClosing('rejected'); setReason(''); }}>Loại</Button>
                <Button size="small" onClick={() => { setClosing('withdrawn'); setReason(''); }}>Ứng viên rút</Button>
              </div>
            )}
          </div>
        )}

        <div>
          <div className="text-sm font-semibold mb-2">Lịch sử</div>
          <div className="space-y-2">
            {[...c.history].reverse().map((h, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: STAGE[h.stage].color }} />
                <div className="min-w-0">
                  <div><b>{STAGE[h.stage].label}</b>{h.score ? <span className={`ml-2 text-xs font-semibold ${h.score >= 7 ? 'text-emerald-600' : h.score >= 5 ? 'text-amber-600' : 'text-rose-600'}`}>{h.score}/10</span> : null}</div>
                  <div className="text-[11px] text-gray-400">{fmtDateTime(h.at)} · {h.by}</div>
                  {h.note && <div className="text-xs text-gray-600">{h.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        {canRecruit && !c.anonymizedAt && c.outcome !== 'hired' && (
          <button type="button" className="text-xs text-gray-400 hover:text-rose-600" onClick={() => eraseCandidate(c, me)}>Ứng viên yêu cầu xóa dữ liệu cá nhân</button>
        )}
      </div>
      {noShow && <NoShowModal empId={noShow} onClose={() => setNoShow(null)} />}
    </Drawer>
  );
}
